import React, { useState } from 'react'
import styled from 'styled-components'

import InvisibleButton from 'components/display/InvisibleButton'
import ThemedCheckbox from 'components/form/ThemedCheckbox'
import CaretDownFilledIcon from 'components/icons/CaretDownFilled'
import CaretUpFilledIcon from 'components/icons/CaretUpFilled'
import useWhichMapMode from 'components/layout/ItemGridWithMap/useWhichMapMode'

import { FilterWrapper } from '../mobile/shared'

const Columns = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`

const CheckboxLabel = styled.label<{ $desktop: boolean }>`
  display: flex;
  flex-shrink: 0;
  padding-right: 16px;

  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-weight: 300;
  line-height: 1.4;

  height: 48px;
  width: 50%;

  ${({ $desktop }) =>
    $desktop &&
    `
    width: 33%;
    min-width: 200px;
  `}

  span {
    margin-left: 14px;
  }
`

interface SelectItemProps {
  label: string
  name: string
  onChange: () => any
  selected?: boolean
}

const SelectItem = ({ label, name, onChange, selected }: SelectItemProps) => {
  const mapMode = useWhichMapMode()
  const inlineMapMode = mapMode === 'inline'

  return (
    <CheckboxLabel $desktop={inlineMapMode}>
      <ThemedCheckbox
        name={name}
        checked={selected}
        onChange={onChange}
        aria-checked={selected}
      />
      <span>{label}</span>
    </CheckboxLabel>
  )
}

const ShowToggle = styled(InvisibleButton)`
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 400;
  line-height: 1.4;
  display: flex;
  align-items: center;

  svg {
    flex-shrink: 0;
    width: 10px;
    height: 5px;
    margin-left: 16px;
  }
`

const Show = styled(Columns)<{ $show: boolean }>`
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  height: ${({ $show }) => ($show ? 'unset' : '0px')};
`

export const TRUNCATE_TO = 6 as const
export const TRUNCATE_IF_MORE_THAN = 10 as const

interface MultiSelectFilterProps {
  name: string
  values: string[]
  valueKey: keyof GQL_SearchFilters
  onChange: (_: Partial<GQL_SearchFilters>) => any
  items: Array<{
    slug: string
    name: string
  }>
  noTruncate?: boolean
}

const MultiSelectFilter = ({
  name,
  values = [],
  valueKey,
  onChange,
  items,
  noTruncate,
}: MultiSelectFilterProps) => {
  const [showRest, setShowRest] = useState(false)

  const toggleShowRest = () => setShowRest(!showRest)

  const onItemClick = (slug: string) => {
    const newValues = values.slice()
    const indexOfSlug = newValues.indexOf(slug)

    if (indexOfSlug > -1) {
      newValues.splice(indexOfSlug, 1)
      return onChange({ [valueKey]: newValues })
    }

    newValues.push(slug)
    return onChange({ [valueKey]: newValues })
  }

  const shouldTruncateItems =
    !noTruncate && items.length > TRUNCATE_IF_MORE_THAN

  const topItems = shouldTruncateItems ? items.slice(0, TRUNCATE_TO) : items
  const restItems = shouldTruncateItems ? items.slice(TRUNCATE_TO) : []

  return (
    <FilterWrapper>
      <Columns>
        {topItems.map((item) => (
          <SelectItem
            key={item.slug}
            label={item.name}
            name={item.slug}
            selected={values.indexOf(item.slug) > -1}
            onChange={() => onItemClick(item.slug)}
          />
        ))}

        <Show $show={showRest}>
          {restItems.map((item) => (
            <SelectItem
              key={item.slug}
              label={item.name}
              name={item.slug}
              selected={values.indexOf(item.slug) > -1}
              onChange={() => onItemClick(item.slug)}
            />
          ))}
        </Show>
      </Columns>

      {restItems?.length > 0 && (
        <ShowToggle
          onClick={toggleShowRest}
          aria-label={`Show ${showRest ? 'less' : 'more'} ${name}`}
          style={{ marginTop: '8px', marginBottom: '8px' }}
        >
          <span>
            Show {showRest ? 'less' : 'more'} {name}
          </span>
          {showRest ? <CaretUpFilledIcon /> : <CaretDownFilledIcon />}
        </ShowToggle>
      )}
    </FilterWrapper>
  )
}

export default MultiSelectFilter
